import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { STATUS_ACTIVE, User } from '../_models';
import { Conversation } from '../_models/conversation';
import { Crush } from '../_models/crush';
import { Model } from '../_models/hydra';
import { UserFavorite } from '../_models/userFavorite';
import { ApiService } from './api.service';
import { AuthenticationService } from './authentication.service';
import { EventsService } from './events.service';
import { SseService } from './sse.service';
import { UserService } from './user.service';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { App } from '@capacitor/app';
import { PluginListenerHandle } from '@capacitor/core';
import { StatsSummary } from '../_models/statsSummary';
import { ROOM_PRIVATE, Room } from '../_models/room';
import { ChatService } from './chat.service';

@Injectable()
export class StateService {
    private ready = false;

    public features: Array<string> = ['global'];

    private subs = new Subscription();

    public users: Array<User> = [];
    public convos: Array<Conversation> = [];
    public favorites: Array<string> = [];
    public crushes: Array<Crush> = [];
    public rooms: Array<Room> = [];

    public pollingOnlineList: Array<string> = [];
    public pollingOnlineTimeout;
    public pollingOnlineInterval = 10;
    
    public stats: StatsSummary;
    public pollingStatsTimeout;
    public pollingStatsInterval = 60;

    public unreadWidgetMessagesCount = 0;

    public pollingDiscussionsTimeout;

    private lastDiscussionEventDate:Date;
    private lastOnlineEventDate:Date;

    private bifrostInterval;
    
    private appListener:PluginListenerHandle;
    private appPauseListener:PluginListenerHandle;
    private appResumeListener:PluginListenerHandle;
    public appActive:boolean = true;
    private appStateChangeSubject: BehaviorSubject<boolean>;
    public appStateChange: Observable<boolean>;
    

    /**
     * Constructor
     */
    constructor(
        private apiService: ApiService,
        private userService: UserService,
        private sseService: SseService,
        private chatService: ChatService,
        private authenticationService: AuthenticationService,
        private eventsService: EventsService,
    ) {
        this.appStateChangeSubject = new BehaviorSubject<boolean>(null);
        this.appStateChange = this.appStateChangeSubject.asObservable();
    }

    /**
     * Startup: load initial app data
     */
    public init() {
        this.ready = true;

        App.addListener('pause', () => {
            this.appActive = false;
            this.appStateChangeSubject.next(this.appActive);
        }).then(handler => this.appPauseListener = handler);

        App.addListener('resume', () => {
            this.appActive = true;
            this.appStateChangeSubject.next(this.appActive);
        }).then(handler => this.appResumeListener = handler);

        // App.addListener('appStateChange', ({ isActive }) => {
        //     this.appActive = isActive;
        //     this.appStateChangeSubject.next(isActive);
        // }).then(handler => this.appListener = handler);

        this.subs.add(this.authenticationService.user.subscribe((user: User) => {
            this.users = [];
            this.convos = [];
            this.favorites = [];
            this.crushes = [];
            this.pollingOnlineList = [];

            if (user) {
                if (this.authenticationService.isAdmin) {
                    this.pollingOnlineInterval = 3;
                } else {
                    this.pollingOnlineInterval = 5;
                }

                this.initBifrost();
                this.getOnlines();
                this.getStats();

                this.getAllFavorites();
                this.getAllMyCrushes();

                this.sseService.events
                    .pipe(
                        filter((data: Model) => {
                            return (
                                data && !!data['@type']
                                // (data['@type'] == 'Crush' || data['@type'] == 'Room' || data['@type'] == 'Visit' || data['@type'] == 'Conversation' || data['@type'] == 'PictureAccessRequest')
                            );
                        })
                    )
                    .subscribe((data: Model) => {
                        if (data['@type'] == 'Crush') {
                            this.manageCrush(data as Crush);
                            this.getStats();
                        } else if (data['@type'] == 'Room') {
                            if (new Date().getTime() - new Date((data as Room).createdAt).getTime() < 60 * 1000) {
                                this.getStats();
                            }
                        } else if (data['@type'] == 'RoomDeletedBy') {
                            this.getStats();
                        } else if (data['@type'] == 'Visit') {
                            this.stats.visits += 1;
                            this.eventsService.updateStats(this.stats);
                        } else if (data['@type'] == 'Conversation') {
                            this.getStats();
                        } else if (data['@type'] == 'PictureAccessRequest') {
                            if (data['status'] == 'pending') {
                                this.stats.albumRequests += 1;
                            } else {
                                this.stats.albumRequests -= 1;
                            }
                            this.eventsService.updateStats(this.stats);
                        }
                    });
            }
        }));
    }

    /**
     * Bifrost check
     */
    initBifrost() {
        if (this.bifrostInterval) {
            clearInterval(this.bifrostInterval);
        }

        this.lastDiscussionEventDate = new Date();
        this.lastOnlineEventDate = new Date();

        this.bifrostInterval = setInterval((_) => {

            if (this.lastOnlineEventDate && this.lastOnlineEventDate.getTime() + 1000 * 30 < new Date().getTime()) {
                this.getOnlines();
                this.getStats();
            }

            
        }, 10000);
    }

    /**
     * Get a user by id
     */
    public getUser(id: string): User {
        return this.users.find(
            (user: User) => user['@id'] == id
        );
    }

    /**
     * Get current online users
     */
    public getOnlines() {
        if (this.pollingOnlineTimeout) {
            clearTimeout(this.pollingOnlineTimeout);
            this.pollingOnlineTimeout = null;
        }
        // if (this.pollingOnlineList && this.pollingOnlineList.length > 0) {
            let pollingIds: Array<string> = this.pollingOnlineList.filter(
                (value, index, self) => self.indexOf(value) === index
            );

            let originalFeatures = [...this.features];
            this.userService.getOnlines(pollingIds, this.features).subscribe(
                (responseData: { [id: string]: boolean }) => {
                    for (let id in responseData) {
                        let user = this.getUser('/users/' + id);
                        if (user) {
                            user.isOnline = responseData[id];
                        }
                    }

                    if (originalFeatures.length > 1) {
                        // this.features = ['global'];
                    }


                    this.waitForOnlinePolling();
                },
                err => {
                    this.waitForOnlinePolling();
                }
            );
        // } else {
        //     this.waitForOnlinePolling();
        // }
    }
    private waitForOnlinePolling() {
        this.lastOnlineEventDate = new Date();

        if (!this.pollingOnlineTimeout && this.ready) {
            this.pollingOnlineTimeout = setTimeout(() => {
                this.getOnlines();
            }, this.pollingOnlineInterval * 1000);
        }
    }

    /**
     * Get user stats
     */
    public getStats() {
        if (this.pollingStatsTimeout) {
            clearTimeout(this.pollingStatsTimeout);
            this.pollingStatsTimeout = null;
        }
        
        this.apiService.getStatsSummary().subscribe(
            (responseData: StatsSummary) => {
                this.waitForStatsPolling();
                this.stats = responseData;

                this.eventsService.updateStats(this.stats);
            },
            err => {
                this.waitForStatsPolling();
            }
        );
    }
    private waitForStatsPolling() {
        if (!this.pollingStatsTimeout && this.ready) {
            this.pollingStatsTimeout = setTimeout(() => {
                this.getStats();
            }, this.pollingStatsInterval * 1000);
        }
    }

    /**
     * Get current user favorites
     */
    private getAllFavorites() {
        this.userService.getFavorites(2000, 1, true).subscribe(
            (favorites: { items: Array<UserFavorite>; totalItems: number }) => {
                if (!this.favorites) {
                    this.favorites = [];
                }
                for (let fav of favorites.items) {
                    this.favorites.push(fav.favorite['@id']);
                    let user = this.getUser(fav.favorite['@id']);
                    if (user) {
                        user.isFavorite = true;
                    }
                }
            },
            error => {}
        );
    }

    /**
     * Get current user crushes
     */
    private getAllMyCrushes() {
        this.userService.getSentorAcceptedCrushes(2000, 1, true).subscribe(
            (crushes: { items: Array<Crush>; totalItems: number }) => {
                if (!this.crushes) {
                    this.crushes = [];
                }
                for (let crush of crushes.items) {
                    this.crushes.push(crush);
                }
            },
            error => {}
        );  
        // this.userService.getSentCrushes(2000, 1, true).subscribe(
        // (crushes: { items: Array<Crush>; totalItems: number }) => {
        //     if (!this.crushes) {
        //         this.crushes = [];
        //     }
        //     for (let crush of crushes.items) {
        //         this.crushes.push(crush);
        //     }

        //     this.userService.getAcceptedCrushes(2000, 1, true).subscribe(
        //         (crushes: { items: Array<Crush>; totalItems: number }) => {
        //             for (let crush of crushes.items) {
        //                 this.crushes.push(crush);
        //             }
        //         },
        //         error => {}
        //     );
        // },
        // error => {}
        // );
    }
    
    /**
     * Set conversation as read
     */
    public touchConversation(id: string) {
        let conversation: Conversation = this.convos.find(
            (convo: Conversation) => convo['@id'] == '/conversations/' + id
        );

        if (conversation) {
            // conversation.isReadByMe = true;
            conversation.readBy.push(this.authenticationService.meValue);
        }
    }

    /**
     * Add a user to the collection
     */
    public addUser(user: User): User {
        if (user) {
            if (this.favorites && this.favorites.includes(user['@id'])) {
                user.isFavorite = true;
            }
    
            if (!user.status) {
                user.status = STATUS_ACTIVE;
            }
            
            if (user.idealAgeMin == 18 && user.idealAgeMax == 120) {
                user.idealAgeMin = null;
                user.idealAgeMax = null;
            }
    
            let existingIndex: number = this.users.findIndex(
                item => item['@id'] == user['@id']
            );
            
            if (existingIndex >= 0) {
                Object.keys(user).forEach(
                    key => (this.users[existingIndex][key] = user[key])
                );
                user = this.users[existingIndex];
            } else {
                this.users.push(user);
            }
    
            user.addedToState = true;
    
            return user;
        }

        return null;
    }

    /**
     * Remove user from the collection
     */
    public removeUser(user: User) {
        let existingIndex: number = this.users.findIndex(
            item => item['@id'] == user['@id']
        );

        if (existingIndex >= 0) {
            this.users.splice(existingIndex, 1);
        }

        user.isLocked = true;

        existingIndex = this.favorites.findIndex(
            item => item == user['@id']
        );

        if (existingIndex >= 0) {
            this.favorites.splice(existingIndex, 1);
        }

        existingIndex = this.crushes.findIndex(
            item => item.source['@id'] == user['@id'] || item.target['@id'] == user['@id']
        );

        if (existingIndex >= 0) {
            this.crushes.splice(existingIndex, 1);
        }

        existingIndex = this.convos.findIndex(
            item => {
                let find = item.members.find((participant:User) => {
                    return participant['@id'] == user['@id'];
                });

                if (find) {
                    return true;
                } else {
                    return false;
                }
            }
        );

        if (existingIndex >= 0) {
            this.crushes.splice(existingIndex, 1);
        }
    }

    /**
     * Add a user to online polling
     */
    public addOnlinePolling(userId: string): void {
        if (userId) {
            environment.debug && console.log("Add to online polling:", userId);
            this.pollingOnlineList.push(userId);
        }
    }

    /**
     * Remove a user to online polling
     */
    public removeOnlinePolling(userId: string): void {
        this.pollingOnlineList = this.pollingOnlineList.filter(id => id !== userId);
    }

    /**
     * Get user data
     */
    public updateUserData(user: User) {
        this.apiService.getUserByUsername(user.username).subscribe(
            (responseData: User) => {
                this.addUser(responseData);
                Object.keys(responseData).forEach(
                    key => (user[key] = responseData[key])
                );
            },
            err => {}
        );
    }

    /**
     * Get if user is a crush
     */
    public isACrush(user: User): boolean {
        if (user) {
            if (this.authenticationService.meValue && user['@id'] == this.authenticationService.meValue['@id']) {
                return false;
            }
            
            return this.crushes.some((crush: Crush) => {
                if (crush.status == 'sent') {
                    return crush.target as any as string == user['@id'] || crush.target['@id'] == user['@id'];
                }

                if (crush.status == 'accepted') {
                    return (
                        crush.target as any as string == user['@id'] ||
                        crush.target['@id'] == user['@id'] ||
                        crush.source as any as string == user['@id'] ||
                        crush.source['@id'] == user['@id']
                    );
                }

                return false;
            });
        } else {
            return false;
        }
    }

    /**
     * Add user to favorites
     */
    public addToFavorites(user: User) {
        let current: User = this.addUser(user);
        this.favorites.push(current['@id']);

        this.eventsService.updateFavorites();
    }

    /**
     * Remove user from favorites
     */
    public removeFromFavorites(user: User) {
        this.favorites.splice(
            this.favorites.findIndex(item => item === user['@id']),
            1
        );
        let current: User = this.addUser(user);

        this.eventsService.updateFavorites();
    }

    /**
     * Manage crush
     */
     public manageCrush(crush: Crush) {
        if (crush.status == 'sent' || crush.status == 'accepted') {
            let existingIndex: number = this.crushes.findIndex(
                item => item['@id'] == crush['@id']
            );
            if (existingIndex >= 0) {
                this.crushes[existingIndex] = crush;
            } else {
                this.crushes.push(crush);
            }
            
        } else if (crush.status == 'canceled') {
            this.crushes = this.crushes.filter((item:Crush) => {
                return item['@id'] != crush['@id'];
            });
        }
    }

    /**
     * Clear
     */
    clear() {

        this.ready = false;

        this.users = [];
        this.convos = [];
        this.favorites = [];
        this.crushes = [];
        this.pollingOnlineList = [];

        this.subs.unsubscribe();
        
        this.sseService.clear();
        this.chatService.clear();

        this.stats = null;

        if (this.pollingDiscussionsTimeout) {
            clearTimeout(this.pollingDiscussionsTimeout);
            this.pollingDiscussionsTimeout = null;
        }

        if (this.pollingOnlineTimeout) {
            clearTimeout(this.pollingOnlineTimeout);
            this.pollingOnlineTimeout = null;
        }

        if (this.bifrostInterval) {
            clearInterval(this.bifrostInterval);
            this.bifrostInterval = null;
        }

        if (this.appListener) {
            this.appListener.remove();
            this.appListener = null;
        }

        if (this.appPauseListener) {
            this.appPauseListener.remove();
            this.appPauseListener = null;
        }

        if (this.appResumeListener) {
            this.appResumeListener.remove();
            this.appResumeListener = null;
        }
    }
}
